import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import './PurchaseSuccess.css';

interface SessionDetails {
  isServiceItem: boolean;
  serviceQueueId?: number;
  itemName?: string;
  hasNote?: boolean;
  customerNotes?: string;
}

const PurchaseSuccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [sessionDetails, setSessionDetails] = useState<SessionDetails | null>(null);
  const [note, setNote] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);
  const [noteSaved, setNoteSaved] = useState(false);
  const [noteError, setNoteError] = useState<string>('');
  const [retryCount, setRetryCount] = useState(0);

  const sessionId = searchParams.get('session_id');
  const API_BASE = process.env.REACT_APP_API_URL || '';

  const checkSessionDetails = useCallback(async () => {
    if (!sessionId || !user) return;

    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.get(`${API_BASE}/api/checkout/session/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSessionDetails(response.data);

      // Pre-populate note field with existing notes (if any)
      if (response.data.isServiceItem && response.data.customerNotes) {
        setNote(response.data.customerNotes);
      }

      setLoading(false);
    } catch (err) {
      // Webhook might not have processed yet, retry a few times
      if (retryCount < 3) {
        setTimeout(() => {
          setRetryCount(prev => prev + 1);
        }, 2000);
      } else {
        // After retries, just show non-service item view
        setSessionDetails({ isServiceItem: false });
        setLoading(false);
      }
    }
  }, [sessionId, user, retryCount, API_BASE]);

  useEffect(() => {
    if (!sessionId) {
      setError('No session ID found');
      setLoading(false);
      return;
    }

    if (!user) {
      // Wait for user context to load
      const timeout = setTimeout(() => {
        if (!user) {
          setSessionDetails({ isServiceItem: false });
          setLoading(false);
        }
      }, 3000);
      return () => clearTimeout(timeout);
    }

    checkSessionDetails();
  }, [sessionId, user, checkSessionDetails]);

  const handleNoteSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!note.trim()) {
      setNoteError('Please enter a note');
      return;
    }

    setNoteSaving(true);
    setNoteError('');

    try {
      const token = localStorage.getItem('auth_token');
      await axios.post(
        `${API_BASE}/api/checkout/session/${sessionId}/note`,
        { note: note.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setNoteSaved(true);
      setNoteSaving(false);
    } catch (err: any) {
      setNoteError(err.response?.data?.error || 'Failed to save note. Please try again.');
      setNoteSaving(false);
    }
  };

  const handleSkipNote = () => {
    setNoteSaved(true);
  };

  const handleContinue = () => {
    navigate('/marketplace');
  };

  const handleGoToGame = () => {
    window.location.href = 'https://www.majicrealm.com';
  };

  if (loading) {
    return (
      <div className="purchase-success-page">
        <div className="container">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Processing your purchase...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="purchase-success-page">
        <div className="container">
          <div className="error-card">
            <div className="error-icon">Warning</div>
            <h1>Something went wrong</h1>
            <p>{error}</p>
            <button onClick={() => navigate('/marketplace')} className="btn-primary">
              Return to Store
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show note input form for service items that haven't submitted a note yet
  const showNoteForm = sessionDetails?.isServiceItem && !noteSaved;

  return (
    <div className="purchase-success-page">
      <div className="container">
        <div className="success-card">
          <div className="success-icon">&#10003;</div>
          <h1>Purchase Successful!</h1>
          <p className="success-message">
            Thank you for your purchase!
            {sessionDetails?.isServiceItem
              ? ` Your order for "${sessionDetails.itemName}" has been received and will be processed by our team.`
              : ' Your items are being processed and will be available in your game shortly.'}
          </p>

          {showNoteForm && (
            <div className="note-form-section">
              <div className="note-form-box">
                <h3>{sessionDetails?.hasNote ? 'Edit Order Details' : 'Add Order Details'}</h3>
                <p className="note-description">
                  {sessionDetails?.hasNote
                    ? 'You can update your customization notes below.'
                    : 'Please provide any custom information we need to fulfill your order (e.g., character name, specific requests, preferences).'}
                </p>
                <form onSubmit={handleNoteSubmit}>
                  <textarea
                    className="note-textarea"
                    placeholder="Enter your custom order details here..."
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    maxLength={255}
                    rows={4}
                    disabled={noteSaving}
                  />
                  <div className="note-char-count">
                    {note.length}/255 characters
                  </div>
                  {noteError && <div className="note-error">{noteError}</div>}
                  <div className="note-buttons">
                    <button
                      type="submit"
                      className="btn-primary"
                      disabled={noteSaving}
                    >
                      {noteSaving ? 'Saving...' : sessionDetails?.hasNote ? 'Update Note' : 'Save Note'}
                    </button>
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={handleSkipNote}
                      disabled={noteSaving}
                    >
                      {sessionDetails?.hasNote ? 'Keep Current' : 'Skip'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {noteSaved && sessionDetails?.isServiceItem && (
            <div className="note-success-box">
              <p>{sessionDetails?.hasNote ? 'Your order details have been updated.' : 'Your order details have been saved.'} We'll process your order soon!</p>
            </div>
          )}

          <div className="session-info">
            <div className="info-box">
              <h3>What happens next?</h3>
              <ul>
                <li>Your purchase has been confirmed</li>
                {sessionDetails?.isServiceItem ? (
                  <>
                    <li>Our team will process your order</li>
                    <li>You'll receive your items once completed</li>
                    <li>Contact us if you have any questions</li>
                  </>
                ) : (
                  <>
                    <li>Items will be delivered to your character in-game</li>
                    <li>This usually happens within a few minutes</li>
                    <li>Log into the Majic Realm to claim your items</li>
                  </>
                )}
              </ul>
            </div>

            {sessionId && (
              <div className="transaction-details">
                <p className="transaction-id">
                  <strong>Transaction ID:</strong> {sessionId.substring(0, 20)}...
                </p>
              </div>
            )}
          </div>

          <div className="action-buttons">
            <button onClick={handleGoToGame} className="btn-primary">
              Go to Majic Realm
            </button>
            <button onClick={handleContinue} className="btn-secondary">
              Continue Shopping
            </button>
          </div>

          <div className="help-text">
            <p>
              Having issues? Contact us at support@majicrealm.com
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PurchaseSuccess;
